import type { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { getImageSize } from '../../util/storyblok/image.utils';
import config from '../../locale.config';
import type { Organization, WithContext } from 'schema-dts';

type DocumentHeadProps = {
  seoTitle?: string;
  seoDescription?: string;
  seoImage?: string;
  seoVideo?: string;
  seoImagePortrait?: string;
  noFollow?: boolean;
};

const DOMAIN = 'https://www.dentsucreative.com';

const jsonLd: WithContext<Organization> = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Dentsu Creative',
  url: DOMAIN,
};

const DocumentHead: NextPage<DocumentHeadProps> = ({
  seoTitle,
  seoDescription,
  seoImage,
  seoImagePortrait,
  seoVideo,
  noFollow,
}) => {
  const router = useRouter();
  const path = router.asPath === '/' ? '' : router.asPath;

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, shrink-to-fit=no"
        />

        {noFollow && (
          <>
            <meta name="robots" content="noindex,nofollow" />
            <meta name="googlebot" content="noindex,nofollow" />
            <meta name="google" content="nositelinkssearchbox" />
          </>
        )}

        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta
          name="ahrefs-site-verification"
          content="12aa8e0fd60bb6f590ba854af292e4cb75a614b45a6c65536f9cc3317d43f9c0"
        />

        {/* <!-- Generic 2:1 Image for Facebook, Linkedin, Twitter --> */}
        {seoImage && (
          <>
            <meta property="og:image" content={seoImage} />
            <meta property="og:image:width" content={`${getImageSize(seoImage).width}`} />
            <meta property="og:image:height" content={`${getImageSize(seoImage).height}`} />
          </>
        )}

        {/* <!-- Secondary 2:3 Image for Pinterest --> */}
        {seoImagePortrait && (
          <>
            <meta property="og:image" content={seoImagePortrait} />
            <meta property="og:image:width" content={`${getImageSize(seoImagePortrait).width}`} />
            <meta property="og:image:height" content={`${getImageSize(seoImagePortrait).height}`} />
          </>
        )}

        {seoVideo && (
          <>
            <meta property="og:video" content={seoVideo} />
            <meta property="og:video:secure_url" content={seoVideo} />
            <meta property="og:video:type" content="video/mp4" />
            <meta property="og:video:width" content="1920" />
            <meta property="og:video:height" content="1080" />
          </>
        )}

        {/* Hreflang tags for all supported locales */}
        <link rel="alternate" href={`${DOMAIN}${path}`} hrefLang="x-default" />
        {config.locales.map((locale) => (
          <link
            key={locale}
            rel="alternate"
            href={`${DOMAIN}${locale === config.defaultLocale ? '' : `/${locale}`}${path}`}
            hrefLang={locale}
          />
        ))}

        {router.locale === config.defaultLocale && (
          <meta
            property="og:url"
            content={`${DOMAIN}${router.asPath === '/' ? '' : router.asPath}`}
          />
        )}
        {router.locale !== config.defaultLocale && (
          <meta
            property="og:url"
            content={`${DOMAIN}/${router.locale}${router.asPath === '/' ? '' : router.asPath}`}
          />
        )}

        <meta property="og:locale" content={router.locale} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Dentsu Creative" />
        <meta property="twitter:title" content={`${seoTitle} - Dentsu Creative`} />
        <meta property="twitter:description" content={seoDescription} />
        <meta property="twitter:image" content={seoImage} />

        {router.locale === config.defaultLocale && (
          <link rel="canonical" href={`${DOMAIN}${router.asPath === '/' ? '' : router.asPath}`} />
        )}
        {router.locale !== config.defaultLocale && (
          <link
            rel="canonical"
            href={`${DOMAIN}/${router.locale}${router.asPath === '/' ? '' : router.asPath}`}
          />
        )}
      </Head>

      <Script
        id="organization-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd),
        }}
      />

      {/* <!-- Global site tag (gtag.js) - Google Analytics --> */}
      <Script
        src="https://www.googletagmanager.com/gtag/js?id=G-G9NKP3ZMT0"
        strategy="afterInteractive"
      />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          
          // https://my.onetrust.com/s/article/UUID-d81787f6-685c-2262-36c3-5f1f3369e2a7
          gtag('consent', 'default', {
            'ad_storage': 'denied',
            'analytics_storage': 'denied',
            'wait_for_update': 500,
          });
          
          gtag('js', new Date());
          gtag('config', 'G-G9NKP3ZMT0', {
            page_path: window.location.pathname,
          });
        `,
        }}
      />

      {/* OneTrust Cookies Consent Notice start for dentsucreative.com */}
      <Script
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        type="text/javascript"
        charSet="UTF-8"
        data-domain-script={
          process.env.NODE_ENV === 'production'
            ? 'ffc384f8-2c71-4c33-be97-09fe45184c40'
            : 'ffc384f8-2c71-4c33-be97-09fe45184c40-test'
        }
      />
      {/* OneTrust Cookies Consent Notice end for dentsucreative.com */}

      {/* <!-- Google Tag Manager --> */}
      <Script
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          (function(w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
              'gtm.start': new Date().getTime(), event: 'gtm.js',
            });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-MBDGVBP');
        `,
        }}
      />
      {/* <!-- End Google Tag Manager --> */}

      {/* <!-- Pardot Script --> */}
      <Script
        id="pardot-script"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          piAId = '580173';
          piCId = '187291';
          piHostname = 'pi.pardot.com';
          
          (function() {
            function async_load(){
              var s = document.createElement('script'); s.type = 'text/javascript';
              s.src = ('https:' == document.location.protocol ? 'https://pi' : 'http://cdn') + '.pardot.com/pd.js';
              var c = document.getElementsByTagName('script')[0]; c.parentNode.insertBefore(s, c);
            }
            if(window.attachEvent) { window.attachEvent('onload', async_load); }
            else { window.addEventListener('load', async_load, false); }
          })();
        `,
        }}
      />

      {/* LinkedIn */}
      <Script
        id="linkedin"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            _linkedin_partner_id = "5270034";
            window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
            window._linkedin_data_partner_ids.push(_linkedin_partner_id);
            (function(l) {
            if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
            window.lintrk.q=[]}
            var s = document.getElementsByTagName("script")[0];
            var b = document.createElement("script");
            b.type = "text/javascript";b.async = true;
            b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
            s.parentNode.insertBefore(b, s);})(window.lintrk);
        `,
        }}
      />
    </>
  );
};

export default DocumentHead;

/*
There are 5 cookie categories that are standard and the defaults provided by OneTrust:

* Strictly necessary cookies ID C0001: the website needs these cookies in order to function properly
(example: identify items placed into a shopping cart)
* Performance cookies ID C0002: get information about how site visitors are using the website
(example: Google analytics)
* Functional cookies ID C0003: provide additional enhancement of the experience of site visitors
(example: language selector for localization)
* Targeting cookies ID C0004: cookies that attempt to gather more information about a user
in order to personalize marketing (example: remarketing)
* Social media cookies ID C0005: social media services added to the site that enable users
to share content with their friends and networks easily

source :
* https://app-de.onetrust.com/cookies/categorizations?tab=Categories
* https://about.gitlab.com/handbook/marketing/marketing-operations/onetrust/
*/
